'use client';

import { useTheme } from 'next-themes';
import { memo, useCallback } from 'react';

import { type PremiereToAirThemeChanged } from '@air/premiere-constants';

import { usePremiereMessaging } from '~/hooks/usePremiereMessaging';

/**
 * Bridges the UXP host's `ThemeChanged` event into `next-themes`. The
 * host reduces Premiere's 5 themes (Lightest, Light, Medium, Dark,
 * Darkest) to Air's 2-mode design system before emitting, so this
 * component only needs to forward the value to `setTheme`. Renders
 * nothing.
 *
 * Lives inside `ThemeProvider` so `useTheme` resolves to the configured
 * provider. Mount it once near the root.
 */
export const ThemeBridge = memo(() => {
  const { setTheme } = useTheme();

  const handleThemeChanged = useCallback(
    (data: PremiereToAirThemeChanged['data']) => {
      setTheme(data.theme);
    },
    [setTheme],
  );

  usePremiereMessaging({ onThemeChanged: handleThemeChanged });

  return null;
});

ThemeBridge.displayName = 'ThemeBridge';
